/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import Body from '../static/Body';
import Card from '../static/Card';
import { motion } from 'framer-motion';
import Text from '../elements/TextComponent';
import { ApiResponse, BotItem } from '../../api/types';
import { addSite, deleteAssistant, getAssistantList, loadSites, removeSite } from '../../api/assistantsApi';
import BotListLoading from '../static/BotListLoading';
import BotListError from '../static/BotListError';
import BotEmbedBuilder, { BotSummary } from '../static/BotEmbedBuilder';
import BotSitesManager from '../static/BotSitesManager';
import V2BotEmbedBuilder from '../static/V2BotEmbedBuilder';

const useFetchAssistantList = () => {

  const [bots, setBots] = useState<BotSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBots = useCallback(async () => {
    setLoading(true);
    const res: ApiResponse<BotItem[]> = await getAssistantList();
    if (res.error) {
      setError(res.error);
    } else {
      setBots(
  Array.isArray(res.data)
    ? res.data.map((b: BotItem) => { return { botId: b.id, name: b.name }; })
    : []
);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  return { bots, loading, error, refresh: fetchBots };
};

interface Props {
//   onSelectAnalytics: (botId: string) => void;
}

const Embed: React.FC<Props> = () => {
    // const handleViewAnalytics = (botId: string) => {
    //   onSelectAnalytics(botId); // Bubble up to Dashboard
    // };
    const { bots, loading, error, refresh } = useFetchAssistantList();
    const [snippet, setSnippet] = useState<string>(`
<script>
(function () {
  function onMsg(ev) {
    var d = ev && ev.data;
    if (!d || d.type !== "ccwm:resize" || typeof d.height !== "number") return;
    var frames = document.querySelectorAll('iframe[src*="/chat/embed"]');
    for (var i = 0; i < frames.length; i++) {
      try {
        if (frames[i].contentWindow === ev.source) {
          frames[i].style.height = Math.max(40, Math.floor(d.height)) + "px";
          frames[i].style.pointerEvents = "auto"; // keep button clickable
          break;
        }
      } catch (e) {}
    }
  }
  window.addEventListener("message", onMsg, false);
})();
</script>

`);

    const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

    return (
        <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                <div style={{ overflowY: 'auto', height: '100vh' }}>
                <Body>
                    <Card width="92%">
                        <Text
                            text="Embed your assistants"
                            fontSize="26px"
                            fontWeight="700"
                            lineHeight="28px"
                            color="#030303"
                            textAlign="left"
                            />
                            <div className='margin-top-10'></div>
                        <Text
                            text="Customize and generate code snippets to add your assistants to your website"
                            fontSize="16px"
                            fontWeight="400"
                            lineHeight="28px"
                            color="#030303"
                            textAlign="left"
                            />
                    </Card>
                    
                        {loading ? <BotListLoading></BotListLoading> : 
                            <>
                            <Card
                            width="92%"
                            height="460px"
                            style={{
                              padding: 16,
                              fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                              display: "flex",
                              flexDirection: "column",
                              gap: 14,
                            }}
                            >
                              <Text
                              text="Authorize your sites to show assistant chats"
                              fontSize="26px"
                              fontWeight="700"
                              lineHeight="28px"
                              color="#030303"
                              textAlign="left"
                              />
                            {/* <div className='margin-top-10'></div> */}
                            <Text
                                text="Required: to use an assistant on a site you must add the site to your bot here"
                                fontSize="16px"
                                fontWeight="400"
                                lineHeight="28px"
                                color="#030303"
                                textAlign="left"
                            />
                            <BotSitesManager loadSites={loadSites} addSite={addSite} removeSite={removeSite} bots={bots}></BotSitesManager>
                            </Card>
                            <V2BotEmbedBuilder bots={bots}></V2BotEmbedBuilder>
                            <BotEmbedBuilder bots={bots}></BotEmbedBuilder>
                            <Card width='92%'>
                              <Text
                              text="Additional useful code"
                              fontSize="26px"
                              fontWeight="700"
                              lineHeight="28px"
                              color="#030303"
                              textAlign="left"
                              />
                            <div className='margin-top-10'></div>
                            <Text
                                text="If launcher=none you can toggle the chat, but due to browser limitations you won't be able to"
                                fontSize="16px"
                                fontWeight="400"
                                lineHeight="28px"
                                color="#030303"
                                textAlign="left"
                            />
                            <Text
                                text="interact with components behind the chat view. To fix this copy and paste this script tag below, and paste underneath the iframe tag."
                                fontSize="16px"
                                fontWeight="400"
                                lineHeight="28px"
                                color="#030303"
                                textAlign="left"
                            />
                            <div className='margin-top-10'></div>
                            {/* Actions */}
                            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>

                              <button
                                onClick={copy}
                                disabled={!snippet}
                                style={{
                                  background: snippet ? "var(--primary)" : "#9CA3AF",
                                  color: "#fff",
                                  border: 0,
                                  borderRadius: 12,
                                  padding: "10px 16px",
                                  fontWeight: 800,
                                  cursor: snippet ? "pointer" : "not-allowed",
                                  opacity: snippet ? 1 : 0.6,
                                  fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                                }}
                              >
                                Copy
                              </button>
                            </div>

                            {/* Output */}
                            <pre
                              style={{
                                margin: 0,
                                background: "var(--tint, #f9f5ff)",
                                color: "var(--text, #111827)",
                                borderRadius: 12,
                                padding: 12,
                                border: "1px solid var(--hover, #7621b9)",
                                outline: "2px solid rgba(118, 33, 185, 0.12)",
                                overflowX: "auto",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                fontSize: 13,
                              }}
                            >
                              {snippet || "<!-- Your embed snippet will appear here after you click Generate -->"}
                            </pre>
                            <div className='margin-bottom-200'></div>
                            </Card>
                            </>
                        }
                        {error ? <BotListError message={error}></BotListError> : null}
                    
                </Body>
                </div>
        </motion.div>
    )
}

export default Embed;
